/* eslint-disable max-len */
 
import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import { createPortal } from 'react-dom';

import { ChildrenType } from 'Type/Common.type';
import { toggleScroll } from 'Util/Browser';
import { noopFn } from 'Util/Common';
import history from 'Util/History';

import './Overlay.style.scss';

/** @namespace Bodypwa/Component/Overlay/Component */
export class OverlayComponent extends PureComponent {
    static propTypes = {
        id: PropTypes.string.isRequired,
        onVisible: PropTypes.func,
        onHide: PropTypes.func,
        activeOverlay: PropTypes.string.isRequired,
        isStatic: PropTypes.bool,
        isRenderInPortal: PropTypes.bool,
        children: ChildrenType,
        isMobile: PropTypes.bool.isRequired,
        scrollToElement: PropTypes.number,
        isFrezeScroll: PropTypes.bool,
        setScrollToElement: PropTypes.func,
        className: PropTypes.string,
        closeOnBrowserBack: PropTypes.bool
    };

    static defaultProps = {
        children: [],
        onVisible: noopFn,
        isStatic: false,
        onHide: noopFn,
        isRenderInPortal: true,
        setScrollToElement: noopFn,
        isFrezeScroll: true,
        scrollToElement: 0,
        className: '',
        closeOnBrowserBack: false
    };

    overlayRef = createRef();

    hasHistoryEntry = false;

    entryBelowKey = null;

    // Key of the entry this overlay pushed, so requestOverlayClose() can detect a navigation since.
    ownEntryKey = null;

    historyUnlisten = null;

    // Set before our own history.goBack(), to tell it apart from a real Back press in handleHistoryChange.
    isProgrammaticGoBack = false;

    componentDidMount() {
        // Overlays mounted already-visible (e.g. AddToCartPopUp) skip componentDidUpdate's transition, so fire onVisible here too.
        if (this.getIsVisible()) {
            this.onVisible();
        }
    }

    componentDidUpdate(prevProps) {
        const prevWasVisible = this.getIsVisible(prevProps);
        const isVisible = this.getIsVisible();

        if (isVisible && !prevWasVisible) {
            this.onVisible();
        }

        if (!isVisible && prevWasVisible) {
            this.onHide();
        }
    }

    onVisible() {
        const {
            onVisible, isStatic, isFrezeScroll = true, closeOnBrowserBack
        } = this.props;

        if (isStatic) {
            return;
        }

        if (isFrezeScroll) {
            this.freezeScroll();
        }

        this.overlayRef.current.focus();

        if (closeOnBrowserBack) {
            this.pushHistoryEntry();
            this.historyUnlisten = history.listen(this.handleHistoryChange);
        }

        onVisible();
    }

    onHide() {
        const { onHide, isStatic, isFrezeScroll = true } = this.props;

        if (isStatic) {
            return;
        }

        this.releaseHistoryEntry();

        if (isFrezeScroll) {
            this.unfreezeScroll();
        }

        onHide();
    }

    // Pushes a history entry so Back closes the overlay instead of navigating away; entryBelowKey identifies it for handleHistoryChange.
    pushHistoryEntry = () => {
        const {
            pathname, search, hash, state, key
        } = history.location;

        this.hasHistoryEntry = true;
        this.entryBelowKey = key;
        history.push({
            pathname,
            search,
            hash,
            state: { ...state, isOverlayOpen: true }
        });
        // push() is synchronous, so history.location is now the pushed entry.
        this.ownEntryKey = history.location.key;
    };

    releaseHistoryEntry = () => {
        if (this.historyUnlisten) {
            this.historyUnlisten();
            this.historyUnlisten = null;
        }
    };

    handleHistoryChange = (location, action) => {
        if (action === 'POP' && this.hasHistoryEntry && location.key === this.entryBelowKey) {
            this.hasHistoryEntry = false;

            const fromBrowserBack = !this.isProgrammaticGoBack;
            this.isProgrammaticGoBack = false;

            this.onOverlayClose(fromBrowserBack);
        }
    };

    // Subclasses override with their close logic; fromBrowserBack lets them treat a Back press as cancel.
    onOverlayClose(fromBrowserBack = false) {} // eslint-disable-line no-unused-vars

    // Subclasses call this instead of onOverlayClose() directly, so UI-triggered closes consume the pushed entry too.
    requestOverlayClose = () => {
        if (this.hasHistoryEntry) {
            if (history.location.key !== this.ownEntryKey) {
                // Navigation moved on already; goBack() now would pop the wrong entry.
                this.hasHistoryEntry = false;
                this.onOverlayClose();
                return;
            }

            // hasHistoryEntry is cleared in handleHistoryChange once the async POP actually lands, not here.
            this.isProgrammaticGoBack = true;
            history.goBack();
            return;
        }

        this.onOverlayClose();
    };

    getIsVisible(props = this.props) {
        const { id, activeOverlay, isStatic } = props;

        return isStatic || id === activeOverlay;
    }

    freezeScroll() {
        document.documentElement.style.setProperty(
            '--window-inner-height',
            `${window.innerHeight}px`
        );
        this.YoffsetWhenScrollDisabled = window.pageYOffset || document.body.scrollTop;
        toggleScroll(false);
    }

    unfreezeScroll() {
        toggleScroll(true);
        const { scrollToElement, setScrollToElement } = this.props;
        if (scrollToElement) {
            window.scrollTo(0, scrollToElement);
            setScrollToElement(0);
        }
    }

    renderInMobilePortal(content) {
        const { isStatic, isRenderInPortal, isMobile } = this.props;

        if (!isStatic && isMobile && isRenderInPortal) {
            return createPortal(content, document.body);
        }

        return content;
    }

    render() {
        const {
            children,
            isStatic,
            className
        } = this.props;

        const isVisible = this.getIsVisible();

        return this.renderInMobilePortal(
            <div
                className={ `bg-neutral-0 will-change-[opacity] z-10 opacity-0
                lg:absolute lg:transition-opacity lg:duration-[200] lg:h-0 lg:overflow-hidden right-0
                ${className}
                ${isStatic ? 'fixed top-[84px] left-0 w-full h-[calc(100%-var(--header-total-height))] pb-(--navigation-tabs-height) overflow-scroll overflow-x-hidden max-w-full' : ''}
                ${isVisible ? 'pointer-events-auto touch-pan-y opacity-100 z-1000 lg:h-auto lg:overflow-visible' : 'opacity-0 pointer-events-none'}
                ` }
                ref={ this.overlayRef }
            >
                { children && children }
            </div>
        );
    }
}

export default OverlayComponent;
